<template>
  <v-row :dense="$vuetify.breakpoint.mobile" class="d-flex align-stretch">
    <v-col cols="6" sm="6" md="3">
      <OverviewCard class="statistic-overview__card" title="Bestellungen" :data="orderChartData" unit="Orders" hide-legend-unit type="doughnut" :comparison="comparisonFor('orders')" />
    </v-col>
    <v-col cols="6" sm="6" md="3">
      <OverviewCard class="statistic-overview__card" title="Umsatz" :switch-data="revenueChartData" unit="€" type="doughnut" :comparison="comparisonFor('revenue', '€')" />
    </v-col>
    <v-col cols="6" sm="6" md="3">
      <OverviewCard class="statistic-overview__card" title="Kosten" :switch-data="costsChartData" unit="€" type="doughnut" :comparison="comparisonFor('costs', '€', true, -1)" />
    </v-col>
    <v-col cols="6" sm="6" md="3">
      <OverviewCard class="statistic-overview__card" title="Gewinn" :switch-data="profitData" :comparison="comparisonFor('profit', '€')" />
    </v-col>
    <v-col cols="12">
      <OverviewBarCard
        title="Zeitliche Verteilung"
        :disable-switch="disableSwitch"
        :data="timelineSwitchData[1].data"
        :switch-data="timelineSwitchData"
        :stacked="true"
        :index-tooltip="true"
        :tooltip-footer="activeTimelineFooter"
        :groups="timelineSwitchData[1] ? timelineSwitchData[1].groups : null"
        unit="€"
        :aspect-ratio="$vuetify.breakpoint.mobile ? 1 : 3"
      />
    </v-col>
<!--    <v-col cols="12">-->
<!--      <AdditionalInformation :data="data"/>-->
<!--    </v-col>-->
  </v-row>
</template>

<script>
import OverviewCard from "@/components/Statistics/StatisticOverview/OverviewCard";
import { statisticsColors, costTypeLabels } from "@/config";
import dayjs from "dayjs";
import OverviewBarCard from "@/components/Statistics/StatisticOverview/OverviewBarCard";
import { mapGetters } from "vuex";
import {getDateFromNow} from "@/utils";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../../../fb";
import { getStatisticTotals } from "@/store/utils/statisticsUtils";

export default {
  name: "StatisticOverview",
  components: { OverviewBarCard, OverviewCard },
  props: {
    data: {
      type: Object,
    },
    comparison: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters(["statisticsDayRange", "statisticComparisonLoading", "statisticRange"]),
    disableSwitch() {
      return this.statisticsDayRange < 1
    },
    totals() {
      return getStatisticTotals(this.data);
    },
    /** AI-API-Kosten (nicht Shop-gebunden) als eigenes Chart-Segment */
    aiCostsSegment() {
      const ai = this.data.aiCosts;
      if (!ai || !ai.total) return null;
      const tooltipLines = Object.values(ai.byKey || {})
        .filter((entry) => entry?.eur)
        .sort((a, b) => b.eur - a.eur)
        .map((entry) => `${entry.name}: -${entry.eur.toFixed(2)} €`);
      return {
        label: "KI-APIs",
        count: -Number(ai.total.toFixed(2)),
        color: statisticsColors.aiCosts,
        tooltipLines,
      };
    },
    comparisonTotals() {
      return this.comparison ? getStatisticTotals(this.comparison) : null;
    },
    orderChartData() {
      // Compute data for order numbers
      return this.data.shops.map((item) => ({
        count: item.totalOrders,
        label: item.domain,
        color: statisticsColors[item.shopCode],
        tooltipLines: this.countryLines(item.ordersByCountry, ""),
      }));
    },
    revenueChartData() {
      return [
        {
          label: "By Shops",
          data: this.data.shops.map((item) => ({
            count: item.revenue.total,
            label: item.domain,
            color: statisticsColors[item.shopCode],
            tooltipLines: this.countryLines(item.revenue.byCountry, " €"),
          })),
        },
        {
          label: "By Source",
          data: this.data.shops.reduce((acc, shop) => {
            let totalTracked = 0;

            for (const revenueSource in shop.revenue.bySource) {
              const revenueValue = shop.revenue.bySource[revenueSource];
              totalTracked += revenueValue;

              const index = acc.findIndex(
                (item) => item.label === revenueSource
              );
              if (index === -1) {
                acc.push({
                  label: revenueSource,
                  count: revenueValue,
                  color: statisticsColors[revenueSource],
                });
              } else {
                acc[index].count += revenueValue;
              }
            }

            // Adding the untracked revenue to a specific 'untracked' item
            const untrackedRevenue = shop.revenue.total - totalTracked;
            const untrackedIndex = acc.findIndex(
              (item) => item.label === "Source Unknown"
            );

            if (untrackedIndex === -1) {
              acc.push({
                label: "Source Unknown",
                count: untrackedRevenue,
                color: "#dedede",
              });
            } else {
              acc[untrackedIndex].count += untrackedRevenue;
            }

            return acc;
          }, []),
        },
      ];
    },
    costsChartData() {
      return [
        {
          label: "By Shops",
          data: this.data.shops.reduce((acc, shop) => {
            const totalCost =
              shop.costs.production +
              shop.costs.taxes +
              shop.costs.additionalCosts +
              shop.costs.shipping +
              shop.costs.employee +
              shop.costs.payments +
              shop.costs.advertisement;
            acc.push({
              label: shop.domain,
              count: -Number(totalCost.toFixed(2)),
              color: statisticsColors[shop.shopCode],
            });
            return acc;
          }, []).concat(this.aiCostsSegment ? [this.aiCostsSegment] : []),
        },
        {
          label: "By Type",
          data: this.data.shops
            .reduce((acc, shop) => {
              for (const costType in shop.costs) {
                const index = acc.findIndex((item) => item.label === costType);
                const costValue = shop.costs[costType] * 100; // Multiply by 100 to work with whole numbers
                if (index === -1) {
                  acc.push({ label: costType, count: costValue });
                } else {
                  acc[index].count += costValue;
                }
              }
              return acc;
            }, [])
            .map((item) => ({
              label: costTypeLabels[item.label] || item.label,
              count: -item.count / 100, // Convert back to the desired format with two decimal places
              color: statisticsColors[item.label],
            }))
            .concat(this.aiCostsSegment ? [this.aiCostsSegment] : []),
        },
      ];
    },
    /* ── Zeitliche Verteilung ──
       Ein Kosten-Stack (Bestellkosten + Werbekanäle) neben dem Umsatz-Balken,
       Gewinn-Linie, Vorperioden-Umsatz als graue Geisterlinie.
       (KI-/Zusatzkosten liegen nicht je Bucket vor — daher "Gewinn",
       der echte Gewinn steht in den KPI-Kacheln.) */
    timelineSeries() {
      const sumShopTimelines = (getter) =>
        this.data.shops.reduce((acc, shop) => {
          const timeline = getter(shop) || {};
          for (const key of Object.keys(timeline)) {
            acc[key] = (acc[key] || 0) + (timeline[key] || 0);
          }
          return acc;
        }, {});
      return {
        orderCosts: sumShopTimelines((s) => s.timelines?.costs),
        revenue: sumShopTimelines((s) => s.timelines?.revenue),
        facebook: sumShopTimelines((s) => s.advertisement?.byChannel?.facebook?.timelineSpend),
        tiktok: sumShopTimelines((s) => s.advertisement?.byChannel?.tiktok?.timelineSpend),
        google: sumShopTimelines((s) => s.advertisement?.byChannel?.google?.timelineSpend),
        chatgpt: sumShopTimelines((s) => s.advertisement?.byChannel?.chatgpt?.timelineSpend),
      };
    },
    /** Gewinn der Vorperiode je Bucket — gleiche Metrik wie die
     *  Hauptlinie, damit der Vergleich Äpfel mit Äpfeln zeigt */
    comparisonProfitTimeline() {
      if (!this.comparison?.shops) return null;
      const sum = (getter) =>
        this.comparison.shops.reduce((acc, shop) => {
          const timeline = getter(shop) || {};
          for (const key of Object.keys(timeline)) {
            acc[key] = (acc[key] || 0) + (timeline[key] || 0);
          }
          return acc;
        }, {});
      const revenue = sum((s) => s.timelines?.revenue);
      const costs = sum((s) => s.timelines?.costs);
      const facebook = sum((s) => s.advertisement?.byChannel?.facebook?.timelineSpend);
      const tiktok = sum((s) => s.advertisement?.byChannel?.tiktok?.timelineSpend);
      const google = sum((s) => s.advertisement?.byChannel?.google?.timelineSpend);
      const chatgpt = sum((s) => s.advertisement?.byChannel?.chatgpt?.timelineSpend);
      const profit = {};
      for (const key of Object.keys(revenue)) {
        profit[key] = (revenue[key] || 0) - (costs[key] || 0) -
          (facebook[key] || 0) - (tiktok[key] || 0) - (google[key] || 0) - (chatgpt[key] || 0);
      }
      return profit;
    },
    timelineKeys() {
      // Konvention des Backends: erst 24 Stunden-Keys (HH:00), danach die
      // Tages-/Monats-Keys des Zeitraums
      const keys = Object.keys(this.timelineSeries.orderCosts);
      return { hourKeys: keys.slice(0, 24), dayKeys: keys.slice(24) };
    },
    timelineSwitchData() {
      const series = this.timelineSeries;
      const { hourKeys, dayKeys } = this.timelineKeys;
      const ghost = this.comparisonProfitTimeline;
      const ghostKeys = ghost ? Object.keys(ghost) : [];
      const ghostHour = ghost ? hourKeys.map((k) => ghost[k] || 0) : null;
      // Tages-Keys der Vorperiode: nach INDEX ausrichten (andere Daten)
      const ghostDayKeys = ghostKeys.slice(24);
      const ghostDay = ghost ? dayKeys.map((k, i) => ghost[ghostDayKeys[i]] || 0) : null;

      const tabs = [
        this.buildTimelineTab("Zeitraum", dayKeys, series, ghostDay),
        this.buildTimelineTab("Tagesverlauf", hourKeys, series, ghostHour),
      ];

      // Wochentags-Muster (nur bei Tages-Keys DD.MM und > 1 Tag)
      if (dayKeys.length > 1 && /^\d{2}\.\d{2}$/.test(dayKeys[0] || "")) {
        const year = dayjs(this.statisticRange?.min).year() || dayjs().year();
        const weekdayLabels = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
        const aggregated = {};
        for (const name of ["orderCosts", "revenue", "facebook", "tiktok", "google", "chatgpt"]) {
          aggregated[name] = {};
        }
        for (const key of dayKeys) {
          const [day, month] = key.split(".").map(Number);
          const weekday = weekdayLabels[(new Date(year, month - 1, day).getDay() + 6) % 7];
          for (const name of Object.keys(aggregated)) {
            aggregated[name][weekday] = (aggregated[name][weekday] || 0) + (series[name][key] || 0);
          }
        }
        tabs.push(this.buildTimelineTab("Wochentage", weekdayLabels, aggregated, null));
      }

      // Kumulativ (auflaufend über den Zeitraum)
      if (dayKeys.length > 1) {
        tabs.push(this.buildCumulativeTab(dayKeys, series, ghostDay));
      }

      return tabs;
    },
    activeTimelineFooter() {
      return this.timelineSwitchData[1]?.tooltipFooter || null;
    },
    profitData() {
      // KI-Kosten fließen anteilig (nach Umsatz) in die Shop-Gewinne ein —
      // kein eigenes Segment, aber korrektes Center-Total
      const aiTotal = this.data.aiCosts?.total || 0;
      const totalRevenue = this.data.shops.reduce(
        (sum, shop) => sum + (shop.revenue.total || 0), 0
      );
      return [
        {
          label: "Profit",
          unit: "€",
          chartType: 'doughnut',
          data: this.data.shops.reduce((acc, shop) => {
            const totalCost =
              shop.costs.production +
              shop.costs.shipping +
              shop.costs.employee +
              shop.costs.payments +
              shop.costs.taxes +
              shop.costs.additionalCosts +
              shop.costs.advertisement;
            const aiShare = totalRevenue > 0
              ? aiTotal * ((shop.revenue.total || 0) / totalRevenue)
              : 0;
            const profit = Number((shop.revenue.total - totalCost - aiShare).toFixed(2));
            acc.push({
              label: shop.domain,
              count: profit,
              color: statisticsColors[shop.shopCode],
              tooltipLines: aiShare > 0.005
                ? [`davon KI-Anteil: -${aiShare.toFixed(2)} €`]
                : null,
            });
            return acc;
          }, [])
        },
        {
          label: "Margin",
          unit: "%",
          chartType: 'bar',
          data: {
            labels: this.data.shops.map(shop => shop.domain),
            datasets: [
              {
                label: "Margin (%)",
                data: this.data.shops.map(shop => {
                  const totalCost =
                    shop.costs.production +
                    shop.costs.shipping +
                    shop.costs.employee +
                    shop.costs.payments +
                    shop.costs.taxes +
                    shop.costs.additionalCosts +
                    shop.costs.advertisement;
                  const aiShare = totalRevenue > 0
                    ? aiTotal * ((shop.revenue.total || 0) / totalRevenue)
                    : 0;
                  const profit = shop.revenue.total - totalCost - aiShare;
                  const margin = shop.revenue.total === 0 ? 0 : Number(((profit / shop.revenue.total) * 100).toFixed(2));
                  return margin;
                }),
                backgroundColor: this.data.shops.map(shop => statisticsColors[shop.shopCode]),
                borderColor: this.data.shops.map(shop => statisticsColors[shop.shopCode]),
                tension: 0.4,
                fill: true,
              },
            ],
          }
        }
      ]
    },
  },
  methods: {
    /** Baut einen Timeline-Tab: Kosten-Stack + Umsatz + Gewinn-Linie */
    buildTimelineTab(label, keys, series, ghostValues) {
      const value = (obj, k) => Number(((obj?.[k] || 0)).toFixed(2));
      const arrays = {
        orderCosts: keys.map((k) => value(series.orderCosts, k)),
        facebook: keys.map((k) => value(series.facebook, k)),
        tiktok: keys.map((k) => value(series.tiktok, k)),
        google: keys.map((k) => value(series.google, k)),
        chatgpt: keys.map((k) => value(series.chatgpt, k)),
        revenue: keys.map((k) => value(series.revenue, k)),
      };
      const totalCosts = keys.map((k, i) =>
        Number((arrays.orderCosts[i] + arrays.facebook[i] + arrays.tiktok[i] + arrays.google[i] + arrays.chatgpt[i]).toFixed(2))
      );
      const profit = keys.map((k, i) => Number((arrays.revenue[i] - totalCosts[i]).toFixed(2)));

      const bar = (name, data, color) => ({
        label: name, data, backgroundColor: color, stack: "kosten",
        borderRadius: 3, maxBarThickness: 26,
      });
      const datasets = [
        bar("Bestellkosten", arrays.orderCosts, statisticsColors.costs),
        bar("Facebook", arrays.facebook, statisticsColors.facebook),
        bar("TikTok", arrays.tiktok, statisticsColors.tiktok),
        bar("Google", arrays.google, statisticsColors.google),
        bar("ChatGPT", arrays.chatgpt, statisticsColors.chatgpt),
        {
          label: "Umsatz", data: arrays.revenue, backgroundColor: statisticsColors.revenue,
          stack: "umsatz", borderRadius: 3, maxBarThickness: 26,
        },
        {
          label: "Gewinn", type: "line", data: profit, stack: "db",
          borderColor: statisticsColors.profit, backgroundColor: statisticsColors.profit,
          borderWidth: 2, pointRadius: 2, pointHoverRadius: 5, tension: 0.3,
        },
      ];
      if (ghostValues) {
        datasets.push({
          label: "Gewinn Vorperiode", type: "line", stack: "ghost",
          data: ghostValues.map((v) => Number((v || 0).toFixed(2))),
          borderColor: statisticsColors.comparison, backgroundColor: statisticsColors.comparison,
          borderDash: [6, 4], borderWidth: 2, pointRadius: 0, tension: 0.3,
        });
      }
      const tooltipFooter = (items) => {
        const index = items[0]?.dataIndex;
        if (index == null) return [];
        const revenue = arrays.revenue[index];
        const margin = revenue ? ((profit[index] / revenue) * 100).toFixed(1) : "0.0";
        return [``, `Kosten gesamt: ${totalCosts[index].toFixed(2).replace(".", ",")} €`, `Marge: ${margin.replace(".", ",")} %`];
      };
      // Serien-Gruppen: gemeinsam ein-/ausblendbar (Chips über dem Chart)
      const groups = [
        { label: "Kosten", color: statisticsColors.costs, datasetLabels: ["Bestellkosten", "Facebook", "TikTok", "Google", "ChatGPT"] },
        { label: "Umsatz", color: statisticsColors.revenue, datasetLabels: ["Umsatz"] },
        { label: "Gewinn", color: statisticsColors.profit, datasetLabels: ["Gewinn"] },
      ];
      if (ghostValues) {
        groups.push({ label: "Vorperiode", color: statisticsColors.comparison, datasetLabels: ["Gewinn Vorperiode"] });
      }
      return { label, data: { labels: keys, datasets }, stacked: true, indexTooltip: true, tooltipFooter, groups };
    },
    /** Kumulativ-Tab: auflaufender Umsatz/Kosten/Gewinn-Linien */
    buildCumulativeTab(keys, series, ghostValues) {
      const running = (arr) => {
        let sum = 0;
        return arr.map((v) => Number((sum += v).toFixed(2)));
      };
      const value = (obj, k) => obj?.[k] || 0;
      const costs = running(keys.map((k) =>
        value(series.orderCosts, k) + value(series.facebook, k) + value(series.tiktok, k) + value(series.google, k) + value(series.chatgpt, k)
      ));
      const revenue = running(keys.map((k) => value(series.revenue, k)));
      const profit = keys.map((k, i) => Number((revenue[i] - costs[i]).toFixed(2)));
      const line = (label, data, color, dash) => ({
        label, type: "line", data, borderColor: color, backgroundColor: color,
        borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: 0.25,
        borderDash: dash || undefined, fill: false,
      });
      const datasets = [
        line("Umsatz (kumuliert)", revenue, statisticsColors.revenue),
        line("Kosten (kumuliert)", costs, statisticsColors.costs),
        line("Gewinn (kumuliert)", profit, statisticsColors.profit),
      ];
      if (ghostValues) {
        datasets.push(line("Gewinn Vorperiode (kum.)", running(ghostValues.map((v) => v || 0)), statisticsColors.comparison, [6, 4]));
      }
      const groups = [
        { label: "Umsatz", color: statisticsColors.revenue, datasetLabels: ["Umsatz (kumuliert)"] },
        { label: "Kosten", color: statisticsColors.costs, datasetLabels: ["Kosten (kumuliert)"] },
        { label: "Gewinn", color: statisticsColors.profit, datasetLabels: ["Gewinn (kumuliert)"] },
      ];
      if (ghostValues) {
        groups.push({ label: "Vorperiode", color: statisticsColors.comparison, datasetLabels: ["Gewinn Vorperiode (kum.)"] });
      }
      return { label: "Kumulativ", data: { labels: keys, datasets }, stacked: false, indexTooltip: true, tooltipFooter: null, groups };
    },
    /** Hover-Zeilen "🇩🇪 DE: 1.234,56 €" aus einer byCountry-Map — nur wenn
     *  der Shop mehr als ein Land bedient */
    countryLines(byCountry, unit) {
      const entries = Object.entries(byCountry || {});
      if (entries.length < 2) return null;
      const flag = (cc) =>
        /^[A-Z]{2}$/.test(cc)
          ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
          : "";
      return entries
        .sort((a, b) => b[1] - a[1])
        .map(([cc, value]) => `${flag(cc)} ${cc}: ${Number(value).toFixed(unit ? 2 : 0)}${unit}`);
    },
    comparisonFor(key, unit = "", invert = false, sign = 1) {
      const previous = this.comparisonTotals ? this.comparisonTotals[key] : null;
      return {
        current: this.totals[key],
        previous,
        // Signed value matching the doughnut center total (costs are shown negative).
        previousTotal: previous === null ? null : previous * sign,
        unit,
        invert,
        loading: this.statisticComparisonLoading,
      };
    },
    async refresh() {
      const getStatistics = httpsCallable(functions, "syncStatistics");
      const res = await getStatistics({
        created_at_min: getters.statisticRange.min,
        created_at_max: getters.statisticRange.max,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.statistic-overview {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  &__card {
    height: 100%;
  }
}
</style>
